/**
 * TenantAgentConfirm renderer · iter303ai-fix9
 *
 * Slim inline confirm-card for Appraisal Booked · Schedule Appraisal · Action 8c.
 * Only rendered when the property is Rental / Business Property (the action's
 * trigger_when handles the skip). Shows tenant contact info + managing agent
 * fields, both editable in place. Save writes to PUT /properties/{id}.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Users, Save, CheckCircle2, Loader2 } from 'lucide-react';
import { advanceOnEnter } from '../intake/scriptEditControls';
import NameTypeahead from '../intake/NameTypeahead';

const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
});
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const TENANT_FIELDS = [
  ['tenant_contact_name',        'Tenant name', 'typeahead', 'tenant_contact_name'],
  ['tenant_contact_number',      'Tenant phone'],
  ['tenant_email',               'Tenant email'],
  ['tenant_preferred_work_time', 'Preferred work time'],
  ['tenant_gate_code',           'Gate code / entry note'],
];

const AGENT_FIELDS = [
  ['agent_name',     'Managing agent name', 'typeahead', 'agent_name'],
  ['agent_company',  'Agent company',       'typeahead', 'agent_company'],
  ['agent_phone',    'Agent phone'],
  ['agent_email',    'Agent email'],
];

export default function TenantAgentConfirm({ action, capturedData, onNext, onBack, onCancel, canBack, continueLabel = 'Confirm & Continue →' }) {
  const propertyId = capturedData?.property_id;
  const [form, setForm] = useState({});
  const [original, setOriginal] = useState({});
  const [propertyType, setPropertyType] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    if (!propertyId) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/properties/${propertyId}`);
      const seed = {};
      for (const [k] of [...TENANT_FIELDS, ...AGENT_FIELDS]) seed[k] = data[k] || '';
      seed.agent_same_as_owner = !!data.agent_same_as_owner;
      setForm(seed); setOriginal(seed);
      setPropertyType(data.property_type || '');
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setLoading(false); }
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);

  const isDirty = Object.keys(form).some(k => (form[k] || '') !== (original[k] || ''));

  const save = async () => {
    if (!isDirty) return;
    setSaving(true); setErr('');
    try {
      await api.put(`/properties/${propertyId}`, form);
      setOriginal({ ...form });
      setSaved(true);
      setTimeout(() => setSaved(false), 1800);
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setSaving(false); }
  };

  if (!propertyId) {
    return (
      <div className="p-6 bg-rose-50 border border-rose-200 rounded text-sm text-rose-700">
        No property linked to this run — cannot confirm tenant/agent info.
        {canBack && <button onClick={onBack} className="ml-3 underline">← Back</button>}
      </div>
    );
  }

  return (
    <div data-testid="wf-tenant-agent-confirm" onKeyDown={advanceOnEnter}>
      <div className="bg-gradient-to-br from-amber-50 to-white border-l-4 border-amber-400 rounded-r-lg p-5 mb-5">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-amber-700 font-semibold mb-1">
          <Users size={12} /> Confirm Tenant &amp; Agent Info
        </div>
        <div className="text-slate-700 text-sm">
          This property is flagged as <span className="font-semibold">{propertyType || 'a rental / business'}</span>.
          Verify the tenant contact and managing-agent details are correct.
        </div>
      </div>

      {loading ? (
        <div className="p-4 text-sm text-slate-400 italic">Loading property…</div>
      ) : (
        <div className="mb-5 space-y-4">
          <div className="border border-slate-200 rounded-lg bg-white p-4">
            <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold mb-3">Tenant Contact</div>
            <div className="grid grid-cols-2 gap-3">
              {TENANT_FIELDS.map(([key, label, kind, source]) => (
                <label key={key} className="flex flex-col gap-1">
                  <span className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold">{label}</span>
                  {kind === 'typeahead' ? (
                    <NameTypeahead
                      value={form[key] || ''}
                      onChange={v => setForm(f => ({ ...f, [key]: v }))}
                      suggestionsSource={source}
                      placeholder="Type to search or add new…"
                      testId={`wf-tenant-field-${key}`}
                    />
                  ) : (
                    <input
                      type="text"
                      value={form[key] || ''}
                      onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                      data-testid={`wf-tenant-field-${key}`}
                      className="border border-slate-200 rounded px-2 py-1.5 text-sm focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-100"
                    />
                  )}
                </label>
              ))}
            </div>
          </div>

          <div className="border border-slate-200 rounded-lg bg-white p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold">Managing Agent</div>
              <label className="flex items-center gap-2 text-xs text-slate-600">
                <input
                  type="checkbox"
                  checked={!!form.agent_same_as_owner}
                  onChange={e => setForm(f => ({ ...f, agent_same_as_owner: e.target.checked }))}
                  data-testid="wf-agent-same-as-owner"
                />
                Agent is the same as owner
              </label>
            </div>
            {!form.agent_same_as_owner && (
              <div className="grid grid-cols-2 gap-3">
                {AGENT_FIELDS.map(([key, label, kind, source]) => (
                  <label key={key} className="flex flex-col gap-1">
                    <span className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold">{label}</span>
                    {kind === 'typeahead' ? (
                      <NameTypeahead
                        value={form[key] || ''}
                        onChange={v => setForm(f => ({ ...f, [key]: v }))}
                        suggestionsSource={source}
                        placeholder="Type to search or add new…"
                        testId={`wf-agent-field-${key}`}
                      />
                    ) : (
                      <input
                        type="text"
                        value={form[key] || ''}
                        onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                        data-testid={`wf-agent-field-${key}`}
                        className="border border-slate-200 rounded px-2 py-1.5 text-sm focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-100"
                      />
                    )}
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {err && (
        <div className="mb-3 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded p-3">{err}</div>
      )}

      <div className="flex items-center gap-3">
        {canBack && (
          <button onClick={onBack} data-testid="wf-back-btn"
            className="px-4 py-2 text-slate-500 hover:text-slate-700">← Back</button>
        )}
        {onCancel && (
          <button onClick={onCancel} data-testid="wf-cancel-btn"
            className="px-4 py-2 text-rose-500 hover:text-rose-700 text-sm">Cancel</button>
        )}
        <span className="flex-1" />
        <button
          onClick={save}
          disabled={!isDirty || saving || loading}
          data-testid="wf-tenant-save-btn"
          className="inline-flex items-center gap-1.5 px-4 py-2 bg-white border-2 border-amber-500 text-amber-700 font-semibold rounded hover:bg-amber-50 disabled:opacity-40 disabled:cursor-not-allowed">
          {saving ? <Loader2 size={14} className="animate-spin" /> : saved ? <CheckCircle2 size={14} className="text-emerald-500" /> : <Save size={14} />}
          {saving ? 'Saving…' : saved ? 'Saved' : 'Save changes'}
        </button>
        <button
          onClick={async () => { if (isDirty) { await save(); } onNext(); }}
          disabled={saving}
          data-testid="wf-tenant-continue-btn"
          className="px-6 py-2 bg-amber-600 text-white font-semibold rounded hover:bg-amber-700 disabled:opacity-40">
          {continueLabel}
        </button>
      </div>
    </div>
  );
}
